import type { LifeDebug } from '../render/habitats/reefLife';

/** An octopus's own doing as the overlay shows it (`ReefOctopuses.debug`). */
export interface OctopusDoing {
  key: number;
  doing: string;
  threat: string | null;
  at: readonly [number, number, number];
}

/**
 * The developer's view of the Reef's interactions (M265, the interactions brief's section 13: "an optional debug display
 * showing: Current behavior. Interaction target. Interaction priority. Target position. Remaining duration. Nearby threat
 * or habitat feature."): each fish's interaction under way, and each octopus's doing. Mounted only in the developer's
 * mode (`?dev`), shown with `?lifeoverlay` or `seaLife.overlay()`, at the foot, under the hunts' at the right; its text is
 * `lifeOverlayText`'s, the one function the tests read.
 */
export function lifeOverlayText(acts: readonly LifeDebug[], octopuses: readonly OctopusDoing[] = []): string {
  const at = (v: readonly [number, number, number]): string => v.map((x) => x.toFixed(2)).join(', ');
  const lines = acts.map((a) => `${a.species} ${a.id} (${a.staged}): ${a.behavior}, priority ${a.priority}, ${a.remainingS.toFixed(1)} s left\n  to ${at(a.position)}${a.target ? `, after ${a.target}` : ''}${a.threat ? `, from ${a.threat}` : ''}${a.feature ? `, at ${a.feature}` : ''}`);
  for (const o of octopuses) if (o.doing !== 'forage') lines.push(`octopus ${o.key}: ${o.doing}${o.threat ? `, from ${o.threat}` : ''}\n  at ${at(o.at)}`);
  return lines.length ? `Interactions: ${acts.length}\n${lines.join('\n')}` : 'Interactions: none under way';
}

export interface LifeOverlay {
  shown(): boolean;
  toggle(on: boolean): void;
  show(acts: readonly LifeDebug[], octopuses?: readonly OctopusDoing[]): void;
}

export function mountLifeOverlay(parent: HTMLElement, visible: boolean): LifeOverlay {
  const node = document.createElement('pre');
  node.className = 'hunt-overlay life-overlay';
  // A diagnostic, as the frame meter is: hidden from assistive technology.
  node.setAttribute('aria-hidden', 'true');
  node.hidden = !visible;
  parent.appendChild(node);
  let painted = '';
  return {
    shown: () => !node.hidden,
    toggle(on: boolean): void {
      node.hidden = !on;
    },
    show(acts: readonly LifeDebug[], octopuses: readonly OctopusDoing[] = []): void {
      if (node.hidden) return;
      // Its first 25 lines, so a busy reef's list fits the screen; the count of all of them on its first line.
      const text = lifeOverlayText(acts, octopuses).split('\n').slice(0, 25).join('\n');
      if (text !== painted) node.textContent = painted = text;
    },
  };
}
